import {Injectable} from '@angular/core';
import {Store} from '@ngrx/store';
import {Variation} from '@sportngin/se-commerce-sdk/models/clare';
import {exhaustMap, forkJoin, Observable, of, throwError} from 'rxjs';
import {map, switchMap, take, tap} from 'rxjs/operators';
import {ProductFormData} from '../../features/product/components/create-product/create-product.types';
import {
  clareProductToProduct,
  mergeVariationsIfMissing,
  ocProductToClareProduct,
  persistPendingProductPriceFromCreate,
  Product,
  ProductSpec,
} from '../../models/clare';
import {selectSelectedOrganizationId} from '../../state/router/router.selectors';
import {SpecApiService} from '../spec-api.service';
import {ProductApiService} from './product-api.service';

export interface CreateProduct {
  save(data: ProductFormData): Observable<Product>;
}

@Injectable({providedIn: 'root'})
export class CreateProductService implements CreateProduct {
  private item: Product;

  constructor(
    private productApi: ProductApiService,
    private store: Store,
    private specApi: SpecApiService
  ) {}

  save(data: ProductFormData): Observable<Product> {
    return this.createProduct(data).pipe(
      exhaustMap(() => this.createSpecs(data)),
      exhaustMap(() => this.generateVariants(data)),
      map(() => {
        const product = clareProductToProduct(this.item);
        persistPendingProductPriceFromCreate(product);
        return product;
      })
    );
  }

  private createProduct(data: ProductFormData): Observable<Product> {
    const item: Product = {
      ...ocProductToClareProduct(data.product),
      active: false,
      catalog_ids: data.product.catalog_ids ?? [],
    };
    const priceCents = data.product.price_cents ?? 0;
    const itemVariation: Variation = {
      name: 'Default',
      price_cents: priceCents,
    };

    if (data.taxCode) {
      (<any>itemVariation).tax_integration_attributes = {
        tax_code: data.taxCode,
      };
    }

    item.variations = [itemVariation];
    return this.store.select(selectSelectedOrganizationId).pipe(
      take(1),
      switchMap(orgId => {
        if (!orgId) {
          return throwError(() => new Error('Organization context could not be loaded. Please refresh and try again.'));
        }

        return this.productApi.createProduct(item, orgId).pipe(
          switchMap(created => this.mergeCreatedItemWithVariations(created, data)),
          tap(merged => (this.item = merged))
        );
      })
    );
  }

  /**
   * When create returns `variations: []` with `default_variation_id`, tries `/variations` (product controller)
   * or falls back to a synthetic default row from the form price.
   */
  private mergeCreatedItemWithVariations(created: Product, data: ProductFormData): Observable<Product> {
    const priceCents = data.product.price_cents ?? 0;
    return mergeVariationsIfMissing(
      created,
      id => this.productApi.getProductVariants(id),
      id => this.productApi.getProductVariant(id)
    ).pipe(
      map(merged => {
        const stillEmpty = (merged.variations?.length ?? 0) === 0;
        const hasDefaultId = merged.default_variation_id !== undefined && merged.default_variation_id !== null;
        let mergedItem: Product = merged;
        if (stillEmpty && hasDefaultId) {
          mergedItem = {
            ...merged,
            variations: [{id: Number(merged.default_variation_id), name: 'Default', price_cents: priceCents}],
          };
        }
        return mergedItem;
      })
    );
  }

  private createSpecs(data: ProductFormData): Observable<unknown> {
    const requests = this.productFormDataToSpecs(data).map(spec => this.specApi.create(spec, Number(this.item.id)));

    // If there are no specs to create, return an observable of null this ensures that the exhaustMap will complete
    if (requests.length === 0) {
      requests.push(of({} as ProductSpec));
    }

    return forkJoin(requests);
  }

  private productFormDataToSpecs(source: ProductFormData): ProductSpec[] {
    return source.specs.map(spec => {
      const options = source.specOptions[spec.name ?? ''] ?? [];
      return {...spec, spec_options: options};
    });
  }

  private generateVariants(data: ProductFormData): Observable<Variation[]> {
    if (data.specs.length === 0) {
      // No specs, no variants
      return of([]);
    }

    return this.productApi.generateProductVariants(this.item.id!);
  }
}
